import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Button, Spin } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import './CampusDetail.less';

const { Title, Paragraph } = Typography;

interface CampusLifeItem {
  id: number;
  title: string;
  content: string;
  imageUrl: string;
  category: string;
  date: string;
}

const CampusDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<CampusLifeItem | null>(null);
  const [allItems, setAllItems] = useState<CampusLifeItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 生成校园生活数据（与Campus.tsx中相同的数据生成逻辑）
    const generateCampusLifeData = (): CampusLifeItem[] => {
      const categories = ['学术活动', '文化艺术', '体育运动', '社团活动', '志愿服务', '校园环境'];
      const titles = [
        '校园春季运动会盛大开幕',
        '学生社团招新活动如火如荼',
        '校园音乐节精彩上演',
        '科技创新大赛获奖作品展',
        '校园图书馆新馆开放',
        '毕业季摄影展温馨开展',
        '校园美食节热闹非凡',
        '校园马拉松比赛圆满结束',
        '学生科技社团成果展示',
        '校园话剧社年度大戏首演',
        '校园篮球联赛激烈进行',
        '绿色校园植树活动',
        '校园英语角活动丰富多彩',
        '校园辩论赛巅峰对决',
        '校园歌手大赛总决赛',
        '校园创客空间正式启用',
        '校园志愿者招募启动',
        '校园文化节系列活动',
        '校园消防安全演练',
        '校园心理健康讲座',
        '校园迎新晚会精彩纷呈',
        '校园计算机技能大赛',
        '校园书法比赛优秀作品展示',
        '校园环保创意大赛',
        '校园模拟联合国大会',
        '校园电竞大赛激情开赛',
        '校园创业分享会',
        '校园爱心捐赠活动',
        '校园绿植领养活动',
        '校园冬季长跑活动',
        '校园机器人比赛',
        '校园诗歌朗诵比赛',
        '校园微电影大赛',
        '校园科研成果展',
        '校园新生篮球赛',
        '校园新年音乐会',
        '校园读书月活动启动'
      ];

      return Array.from({ length: 36 }, (_, index) => ({
        id: index + 1,
        title: titles[index],
        content: `这是关于${titles[index]}的详细描述。活动在羊小羊校园内举行，吸引了众多师生参与。活动现场氛围热烈，同学们积极互动，展现了羊小羊学子的青春活力和团队精神。通过此类活动，丰富了校园文化生活，促进了同学之间的交流与合作。\n\n活动详情：\n本次活动由校学生会主办，得到了学校各部门的大力支持。活动现场设置了多个互动环节，包括知识问答、才艺展示、团队协作等，让同学们在轻松愉快的氛围中增进了解、培养友谊。\n\n参与感受：\n同学们纷纷表示，这样的校园活动非常有意义，不仅丰富了课余生活，还提供了展示自我的平台。希望学校今后能多举办类似的活动，为校园增添更多活力。\n\n活动总结：\n通过这次活动，我们看到了羊小羊学子积极向上的精神风貌，也感受到了校园文化的魅力。未来，我们将继续举办更多丰富多彩的校园活动，为同学们创造更好的学习生活环境。`,
        imageUrl: `https://picsum.photos/seed/campus${index + 1}/1200/800`,
        category: categories[index % categories.length],
        date: `${2024}-${String((index % 12) + 1).padStart(2, '0')}-${String((index % 28) + 1).padStart(2, '0')}`
      }));
    };

    // 模拟API请求延迟
    const timer = setTimeout(() => {
      const allItems = generateCampusLifeData();
      // 先设置所有项目，用于计算上一个和下一个
      setAllItems(allItems);
      const foundItem = allItems.find(item => item.id.toString() === id);
      setItem(foundItem || null);
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [id]);

  const handleBack = () => {
    navigate('/campus'); // 直接导航到校园生活列表页面
  };

  const handlePrevious = () => {
    if (item && item.id > 1) {
      navigate(`/campus/${item.id - 1}`);
    }
  };

  const handleNext = () => {
    if (item && allItems.length > 0 && item.id < allItems.length) {
      navigate(`/campus/${item.id + 1}`);
    }
  };

  if (loading) {
    return (
      <div className="detail-loading-container">
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="detail-not-found">
        <Title level={3}>活动不存在</Title>
        <Button type="primary" icon={<ArrowLeftOutlined />} onClick={handleBack}>
          返回列表
        </Button>
      </div>
    );
  }

  return (
    <div className="campus-detail-container">
      {/* 鼠标悬浮检测区域 - 用于触发按钮显示 */}
      <div className="mouse-hover-left-area"></div>
      <div className="mouse-hover-right-area"></div>
      
      {/* 返回列表按钮 - 固定在左侧 */}
      <div className="back-button-container">
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} className="back-btn">
          返回列表
        </Button>
      </div>
      
      {/* 内容和导航按钮的布局 - 按钮在两侧 */}
      <div className="content-with-nav-buttons">
        {/* 左侧 - 上一个按钮 */}
        <div className="left-nav-button">
          {item.id > 1 && (
            <Button 
              type="primary" 
              icon={<ArrowLeftOutlined />} 
              onClick={handlePrevious}
              className="side-nav-btn"
            />
          )}
        </div>
        
        {/* 中间 - 内容卡片 */}
        <Card className="detail-card" bordered={false}>
        <div className="detail-header">
          <div className="detail-meta">
            <span className="category-badge">{item.category}</span>
            <span className="detail-date">{item.date}</span>
          </div>
          <Title level={2} className="detail-title">{item.title}</Title>
        </div>
        
        <div className="detail-image-container">
          <img 
            alt={item.title} 
            src={item.imageUrl} 
            className="detail-image"
          />
        </div>
        
        <div className="detail-content">
          {item.content.split('\n').map((paragraph, idx) => (
            <Paragraph key={idx} className="detail-paragraph">
              {paragraph}
            </Paragraph>
          ))}
        </div>
      </Card>
        
        {/* 右侧 - 下一个按钮 */}
        <div className="right-nav-button">
          {allItems.length > 0 && item.id < allItems.length && (
            <Button 
              type="primary" 
              icon={<ArrowRightOutlined />} 
              onClick={handleNext}
              className="side-nav-btn"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CampusDetail;